import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { THEME_COLOR_NAMES, ThemeColorName } from '@examdojo/styles';
import type { JsxgraphTheme, LabelStyleResult, LabelVariant } from './jsxgraph.model';

/**
 * Service that provides theme values for JSXGraph widgets.
 * Reads CSS variables at runtime to support light/dark modes.
 *
 * This service only returns RAW VALUES (colors, typography, spacing).
 * It does NOT return JSXGraph API options - those are built in widget-base.
 */
@Injectable()
export class JsxgraphThemeService {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  private cachedTheme: JsxgraphTheme | null = null;

  // =============================================================================
  // THEME API
  // =============================================================================

  /**
   * Get the complete theme configuration.
   * Cached for performance - call invalidateCache() if CSS variables change.
   */
  getTheme(): JsxgraphTheme {
    if (this.cachedTheme) {
      return this.cachedTheme;
    }

    this.cachedTheme = {
      colors: {
        axis: this.getCssVar('neutral', 300),
        grid: this.getCssVar('neutral', 300),
        background: this.getCssVar('neutral', 0),
        border: this.getCssVar('neutral', 200),
        textSecondary: this.getCssVar('secondary'),
        feature: THEME_COLOR_NAMES.map((name) => this.getCssVar(name, 500)),
        shading: THEME_COLOR_NAMES.reduce(
          (acc, name) => ({ ...acc, [name]: this.getCssVar(name, 50) }),
          {} as Record<ThemeColorName, string>,
        ),
      },
      typography: {
        fontFamily: 'Inter, sans-serif',
        fontSize: {
          base: 16,
          small: 14,
          label: 12,
        },
        fontWeight: {
          normal: 400,
          medium: 500,
          bold: 600,
        },
      },
      spacing: {
        borderRadius: '16px',
        labelPadding: '2px 6px',
        labelOffset: [9, 19],
        sliderLength: 3,
      },
      labelColors: {
        white: this.getCssVar('neutral', 0),
        bgLight: (color: ThemeColorName) => this.getCssVar(color, 100),
        textDark: (color: ThemeColorName) => this.getCssVar(color, 600),
        textDarker: (color: ThemeColorName) => this.getCssVar(color, 700),
      },
    };

    return this.cachedTheme;
  }

  /**
   * Invalidate the cached theme (call when CSS variables change, e.g., theme switch).
   */
  invalidateCache(): void {
    this.cachedTheme = null;
  }

  // =============================================================================
  // COLOR HELPERS
  // =============================================================================

  /**
   * Get a feature color by theme color name.
   */
  getColor(color: ThemeColorName): string {
    return this.getCssVar(color, 500);
  }

  /**
   * Get a shading color by theme color name.
   */
  getShade(color: ThemeColorName): string {
    return this.getCssVar(color, 50);
  }

  /**
   * Get label colors based on variant and color.
   */
  getLabelStyle(variant: LabelVariant, color: ThemeColorName): LabelStyleResult {
    const theme = this.getTheme();

    switch (variant) {
      case 'primary':
        return {
          backgroundColor: this.getColor(color),
          textColor: theme.labelColors.white,
        };
      case 'secondary':
        return {
          backgroundColor: theme.labelColors.bgLight(color),
          textColor: theme.labelColors.textDark(color),
        };
      case 'tertiary':
        return {
          backgroundColor: 'transparent',
          textColor: theme.labelColors.textDarker(color),
        };
    }
  }

  // =============================================================================
  // INTERNAL HELPERS
  // =============================================================================

  private getCssVar(name: string, shade?: number): string {
    const cssVar = shade !== undefined ? `--${name}-${shade}` : `--${name}`;
    const root = this.document.documentElement;
    return getComputedStyle(root).getPropertyValue(cssVar).trim();
  }
}
